/**
 * Headless. Mounted once in AppShell alongside AutoSelectClient.
 *
 * For group-admin users, fetches the group's org list on first render and
 * auto-selects index 0 if nothing is already stored in localStorage. This
 * is what unlocks every downstream agent-scoped page (FIM, Auth, Security
 * Compliance, …) for group admins — those pages key their queries off an
 * org id, and before this ran there was no org id to key against.
 *
 * No-ops for:
 *   • org admins   (user.org_id is already set via JWT)
 *   • platform    (no single-org concept)
 *   • viewers     (no agent pages to feed)
 *   • already-picked group admins (preserve their pick across reloads)
 *
 * Re-fires the auto-pick when the stored org id no longer appears in the
 * fetched list (admin removed the org from the group mid-session), so
 * the UI doesn't stay pinned to a now-invalid org.
 */
import { useEffect } from "react";
import { useAuth } from "@features/auth/AuthContext";
import { usePickerOrgs, useSelectedOrg } from "./hooks/useOrgPicker";

export const AutoSelectOrg: React.FC = () => {
  const { user } = useAuth();
  // group_id isn't on the formal User type yet (same situation as
  // group_name in TopNav), so cast locally. Falls back to the sidecar
  // localStorage key safeguarded under a try/catch.
  const groupId =
    (user as { group_id?: string } | null | undefined)?.group_id ??
    (() => { try { return localStorage.getItem("specta.group_id") ?? ""; } catch { return ""; } })();

  const isGroupAdmin = user?.scope_level === "group";
  const { selected, select } = useSelectedOrg();
  const list = usePickerOrgs(isGroupAdmin ? groupId : "");

  useEffect(() => {
    if (!isGroupAdmin) return;
    const rows = list.data;
    if (!rows || rows.length === 0) return;

    // Nothing picked yet → pick row 0.
    if (!selected) {
      const first = rows[0]!;
      select({ org_id: first.org_id, name: first.name });
      return;
    }

    // Stored pick no longer exists in this group → repick row 0.
    const stillValid = rows.some((o) => o.org_id === selected.org_id);
    if (!stillValid) {
      const first = rows[0]!;
      select({ org_id: first.org_id, name: first.name });
    }
  }, [isGroupAdmin, list.data, selected, select]);

  return null;
};
